import {
  date,
  date_time,
  numeric,
  percentage,
  scientific,
  string,
} from "./config";
import moment from "moment";
import { isValid } from "date-fns";

const IsNumericData = (trimmedValue, validResult, range) => {
  // Check if the value is a valid number and not in scientific notation
  if (isNaN(trimmedValue) || /e/i.test(trimmedValue)) {
    validResult = { error: true, helperText: numeric.errorMessage };
  } else {
    const numericValue = parseFloat(trimmedValue); // Convert the value to a numeric format

    if (range && range.min !== undefined && range.min !== "") {
      // Check if the numeric value is greater than the minimum value
      if (numericValue < parseFloat(range.min)) {
        validResult = { error: true, helperText: numeric.errorMessage };
      }
    }

    if (range && range.max !== undefined && range.max !== "") {
      // Check if the numeric value is less than the maximum value
      if (numericValue > parseFloat(range.max)) {
        validResult = { error: true, helperText: numeric.errorMessage };
      }
    }
  }

  return validResult;
};

const IsStringData = (trimmedValue, validResult) => {
  if (trimmedValue?.length < string.min || trimmedValue?.length > string.max) {
    validResult = { error: true, helperText: string.errorMessage };
  }

  return validResult;
};

const IsDateData = (trimmedValue, validResult) => {
  let isValidDate = isValid(new Date(trimmedValue)); // Using date-fns
  const isValidDateFormat = moment(trimmedValue).isValid(); // without applying format for date

  if (!isValidDate || !isValidDateFormat) {
    validResult = { error: true, helperText: date.errorMessage };
  }

  return validResult;
};

const IsDateAndTimeData = (trimmedValue, validResult) => {
  let isValidDateTime = false;
  isValidDateTime = moment(trimmedValue).isValid(); // without applying date time formats

  if (!isValidDateTime) {
    validResult = { error: true, helperText: date_time.errorMessage };
  }

  return validResult;
};

const IsTimeData = (trimmedValue, validResult) => {
  let isValid = false;

  // Try parsing the input using moment.js
  const momentTime = moment(
    trimmedValue,
    ["HH:mm:ss", "HH:mm", "h:mm:ss A", "h:mm A"],
    true
  );
  if (momentTime.isValid()) {
    isValid = true;
  }

  // Try parsing the input using date-fns
  if (!isValid) {
    const dateFnsTime = new Date(trimmedValue);
    if (!isNaN(dateFnsTime) && /^\d{2}:\d{2}(:\d{2})?$/.test(trimmedValue)) {
      isValid = true;
    }
  }

  if (!isValid) {
    validResult = { error: true, helperText: "Invalid time format" };
  }

  return validResult;
};

const IsPercentageData = (trimmedValue, validResult, range) => {
  if (
    !/^\d+(\.\d+)?%$/.test(trimmedValue) &&
    (isNaN(trimmedValue) || /e/i.test(trimmedValue))
  ) {
    validResult = { error: true, helperText: percentage.errorMessage };
  } else {
    // Remove the "%" sign and convert to a numeric value
    const numericValue = parseFloat(trimmedValue.replace("%", ""));

    if (range && range.min !== undefined && range.min !== "") {
      if (numericValue < parseFloat(range.min)) {
        validResult = { error: true, helperText: percentage.errorMessage };
      }
    }
    if (range && range.max !== undefined && range.max !== "") {
      if (numericValue > parseFloat(range.max)) {
        validResult = { error: true, helperText: percentage.errorMessage };
      }
    }
  }

  return validResult;
};

const IsScientificData = (trimmedValue, validResult) => {
  let isValueContainDot = false;
  let isValueContainExpoParameter = false;
  let isLastValueContainTwoDigits = false;
  let isStringContaiainRemeaningNumbers = false;
  let isBetweenDotAndEContainNumbers = false;
  let isStringContainOnlyOneDot = false;

  if (trimmedValue.includes(".")) {
    isValueContainDot = true;
  }

  if (trimmedValue.includes("e+") || trimmedValue.includes("E+")) {
    isValueContainExpoParameter = true;
  }

  if (
    parseInt(trimmedValue.substring(trimmedValue.indexOf("+") + 1)) &&
    trimmedValue.substring(trimmedValue.indexOf("+") + 1).length === 2 &&
    !isNaN(trimmedValue.substring(trimmedValue.indexOf("+") + 1))
  ) {
    isLastValueContainTwoDigits = true;
  }

  if (
    parseInt(trimmedValue.substr(0, trimmedValue.indexOf("."))) >= 0 &&
    !isNaN(trimmedValue.substr(0, trimmedValue.indexOf(".")))
  ) {
    isStringContaiainRemeaningNumbers = true;
  }

  if (
    parseInt(
      trimmedValue
        .toLowerCase()
        .substring(
          trimmedValue.toLowerCase().indexOf(".") + 1,
          trimmedValue.toLowerCase().lastIndexOf("e")
        )
    ) &&
    !isNaN(
      trimmedValue
        .toLowerCase()
        .substring(
          trimmedValue.toLowerCase().indexOf(".") + 1,
          trimmedValue.toLowerCase().lastIndexOf("e")
        )
    )
  ) {
    isBetweenDotAndEContainNumbers = true;
  }

  if (
    (trimmedValue?.match(/\./g) || []).length &&
    (trimmedValue?.match(/\./g) || []).length === 1
  ) {
    isStringContainOnlyOneDot = true;
  }

  if (
    !(
      isValueContainDot &&
      isValueContainExpoParameter &&
      isLastValueContainTwoDigits &&
      isStringContaiainRemeaningNumbers &&
      isBetweenDotAndEContainNumbers &&
      isStringContainOnlyOneDot
    )
  ) {
    validResult = { error: true, helperText: scientific.errorMessage };
  }

  return validResult;
};

const Validation = (dataType, range, value) => {
  let validResult = { error: false, helperText: "" };
  if (value) {
    {
      let trimmedValue;
      if (typeof value === "string") {
        trimmedValue = value?.trim();
      } else trimmedValue = value;

      switch (dataType) {
        case "string":
          return IsStringData(trimmedValue, validResult);

        case "numeric":
          return IsNumericData(trimmedValue, validResult, range);

        case "date":
          return IsDateData(trimmedValue, validResult);

        case "date_time":
          return IsDateAndTimeData(trimmedValue, validResult);

        case "percentage":
          return IsPercentageData(trimmedValue, validResult, range);

        case "scientific":
          return IsScientificData(trimmedValue, validResult);

        case "time":
          return IsTimeData(trimmedValue, validResult);

        default:
          validResult = { error: false, helperText: "" };
      }
    }
    return validResult;
  } else {
    validResult = { error: false, helperText: "" };
  }
};
export default Validation;
